import React, { useRef, useState } from "react";
import { Button } from "Components/atoms/Button";
import { Radio } from "Components/atoms/Radio";
import { DataSearchMobile } from "Components/molecules/DataSearch/Mobile";
import { DataSearchOriginMobile } from "Components/molecules/DataSearch/Mobile/Origin";
import { DestinationFieldSearchMobile } from "Components/molecules/FieldSearch/Mobile/Destination";
import { OriginFieldSearchMobile } from "Components/molecules/FieldSearch/Mobile/Origin";
import { InputFieldSearchMobile } from "Components/molecules/FieldSearch/Mobile";
import { GuestSelectorWithInputMobile } from "Components/molecules/GuestSelectorWithInput/Mobile";

import { useSearchContext } from "shared/contexts/SearchContext";
import { useClickOutside } from "shared/hooks/useClickOutside";
import {
  ContentWrapperMobile,
  DatasSearchWrapperMobile,
  RadioWrapperMobile,
  SearchContentWrapperMobile,
} from "./styles";

import { Airport } from "shared/types/airportsTypes";
import { IoIosSearch } from "react-icons/io";
import {
  CalendarProvider,
  useCalendarContext,
} from "shared/contexts/CalendarContext";
import { composeURL } from "shared/utils/urlComposer";
import { Modal } from "Components/molecules/Modal/Mobile";
import { useGeneralContext } from "shared/contexts/GeneralContext";
import { DropdownMobile } from "Components/atoms/Dropdown/Mobile";
import { sendGTMEvent } from "shared/services/GTM";
import { handleAltRedirect } from "shared/utils/handleAltRedirect";
import { useAltRedirectHook } from "shared/services/altRedirect/useAltRedirectHook";
import { AltRedirectCampaigns } from "shared/services/altRedirect/types";
import { useBreakpoint } from "shared/hooks/useBreakpoint";
import { captureSentryException } from "shared/services/sentry/captureException";

/**
 * Componente SearchFlightsMobile
 *
 * Interface de busca para voos em plataformas mobile.
 */
export const SearchFlightsMobile: React.FC = () => {
  const {
    origins,
    nationalDestinations,
    internationalDestinations,
    searchState,
    setTripType,
    setOrigin,
    setDestination,
    setVisibility,
  } = useSearchContext();

  const {
    isShowOriginCalendarPickerMobile,
    isShowDestinationCalendarPickerMobile,
    setShowOriginCalendarPickerMobile,
    setShowDestinationCalendarPickerMobile,
  } = useCalendarContext();

  const {
    isOriginModalVisible,
    setIsOriginModalVisible,
    isDestinationModalVisible,
    setIsDestionationModalVisible,
  } = useGeneralContext();

  const originRef = useRef<HTMLDivElement>(null);
  const destinationRef = useRef<HTMLDivElement>(null);

  useClickOutside([originRef, destinationRef], () => {
    setVisibility("origin", false);
    setVisibility("destination", false);
  });

  const [filteredOrigins, setFilteredOrigins] = useState<Airport[]>(origins);
  const [isSearchTermTyped, setSearchTermTyped] = useState<boolean>(false);

  const { altRedirectCampaign } = useAltRedirectHook<AltRedirectCampaigns>();

  const [isMobile, setMobile] = useState(false);
  const breakpoint = useBreakpoint();

  const setAltRedirectGTM = (anunciante: string) => {
    try {
      const departureDates = searchState.departureDates || [];
      const returnDates = searchState.returnDates || [];

      const event = {
        anunciante: anunciante ?? "",
        airport_origin: searchState.origin?.code ?? "",
        airport_destination: searchState.destination?.code ?? "",
        flight_depart_date: departureDates,
        flight_return_date: returnDates,
        device: "mobile",
      };

      sendGTMEvent("alt_redirect", event);
    } catch (error) {
      captureSentryException(error, {
        tags: {
          feature: "search-flights",
          operation: "sendAltRedirectGTM",
          device: "mobile",
        },
      });
      console.error("Erro ao enviar evento alt_redirect ao GTM");
    }
  };

  const handleSearch = () => {
    if (
      !searchState.origin ||
      searchState.origin.code === "" ||
      !searchState.destination ||
      searchState.destination.code === "" ||
      !searchState.departureDates?.length
    ) {
      alert("Por favor, preencha todos os campos obrigatórios!");
      return;
    }

    const altRedirect = handleAltRedirect(
      searchState.origin.code,
      searchState.destination.code,
      altRedirectCampaign,
      isMobile,
      {
        departureDates: searchState.departureDates,
        returnDates: searchState.returnDates,
        adults: searchState.adults,
        children: searchState.children,
        babies: searchState.babies,
      },
    );

    const url = composeURL(searchState);

    if (altRedirect.execute) {
      window.open(url, "_blank");
      if (altRedirect.url) {
        setAltRedirectGTM(altRedirect?.name ?? "");
        window.location.href = altRedirect.url;
      }
    } else {
      window.open(url, "_self");
    }
  };

  const inputOriginElement = () => {
    return (
      <InputFieldSearchMobile
        ref={originRef}
        origins={origins}
        origin={searchState.origin?.description || ""}
        isVisible={isOriginModalVisible}
        setIsVisible={(isVisible) => setVisibility("origin", isVisible)}
        onSelect={(origin: Airport) => {
          console.log(origin);
          setOrigin(origin);
          setIsOriginModalVisible(false);
        }}
        type="origin"
      />
    );
  };

  const inputOriginDestinationElement = () => {
    return (
      <InputFieldSearchMobile
        ref={destinationRef}
        origins={origins}
        nationalDestinations={nationalDestinations}
        internationalDestinations={internationalDestinations}
        destination={searchState.destination?.description || ""}
        isVisible={isDestinationModalVisible}
        setIsVisible={(isVisible) => setVisibility("destination", isVisible)}
        onSelect={(destination: Airport) => {
          setDestination(destination);
          setIsDestionationModalVisible(false);
        }}
        type="destination"
      />
    );
  };

  return (
    <SearchContentWrapperMobile>
      <RadioWrapperMobile>
        <Radio
          name="options"
          value="roundTrip"
          label="Ida e volta"
          isSelected={searchState.tripType === "roundTrip"}
          onChange={setTripType}
        />
        <Radio
          name="options"
          value="oneWay"
          label="Só ida"
          isSelected={searchState.tripType === "oneWay"}
          onChange={setTripType}
        />
      </RadioWrapperMobile>

      <Modal
        isVisibleModal={isOriginModalVisible}
        onClose={() => setIsOriginModalVisible(false)}
      >
        {inputOriginElement()}
      </Modal>

      <Modal
        isVisibleModal={isDestinationModalVisible}
        onClose={() => setIsDestionationModalVisible(false)}
      >
        {inputOriginDestinationElement()}
      </Modal>

      <ContentWrapperMobile>
        <DatasSearchWrapperMobile>
          {!isOriginModalVisible && inputOriginElement()}
          {!isDestinationModalVisible && inputOriginDestinationElement()}

          <DataSearchMobile
            type="origin"
            isVisibel={isShowOriginCalendarPickerMobile}
          />
          <DataSearchMobile
            type="destination"
            isVisibel={isShowDestinationCalendarPickerMobile}
          />
        </DatasSearchWrapperMobile>

        <GuestSelectorWithInputMobile />
      </ContentWrapperMobile>

      <Button
        label="Buscar passagens"
        backgroundColor="yellow"
        color="black"
        size="medium"
        icon={<IoIosSearch />}
        uppercase
        height={50}
        width="100%" // <-- Adicione isso
        rounded={false} // <-- Evite o border-radius do botão, vamos controlar manualmente
        onClick={handleSearch}
      />
    </SearchContentWrapperMobile>
  );
};
